import mitt from 'mitt';

import type { EventMsg, EventType, TEvent } from './types';
import { isMsgType } from './types';

export * from './types';
import { eventsPoller } from '~shared/services/eventsPoller';
import { ws } from '~shared/services/ws/websocket';

export class EventsInbox {
	private fallbackFn?: (sync?: string) => Promise<TEvent[]>;
	private unsubscribe: (() => void) | undefined;
	messageEmitted = mitt<{ event: EventMsg }>();

	onConnectionFailed() {
		if (this.fallbackFn) {
			this.clearSubscriptions();
			this.unsubscribe = eventsPoller.onMsg((msg) => {
				this.messageEmitted.emit('event', msg);
			});
			eventsPoller.start(this.fallbackFn);
		}
	}

	async connect(
		getUrl?: () => Promise<string>,
		fallbackFn?: (sync?: string) => Promise<TEvent[]>
	) {
		this.clearSubscriptions();
		this.fallbackFn = fallbackFn;
		this.unsubscribe = ws.onMsg((msg) => {
			this.messageEmitted.emit('event', msg);
		});
		await ws.connect(getUrl, this.onConnectionFailed.bind(this));
	}

	onMsg(cb: (msg: EventMsg) => void) {
		this.messageEmitted.on('event', cb);

		return () => {
			this.messageEmitted.off('event', cb);
		};
	}

	onTypeMsg<T extends EventType, M extends EventMsg>(
		type: T,
		cb: (data: M extends { type: T } ? M['payload'] : never) => void
	) {
		return this.onMsg((msg) => {
			if (!isMsgType(msg, type)) return;

			const payload = msg.payload as M extends { type: T }
				? M['payload']
				: never;
			cb(payload);
		});
	}

	clearSubscriptions() {
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = undefined;
		}
	}

	close() {
		ws.close();
	}
}

export const eventsInbox = new EventsInbox();
